import React from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import languageNameMap from '../intl/locale.json'

const isMobile = () => {
  return window.innerWidth < 951
}

const getMediaType = (str) => {
  return str.split('.').pop()
}

const getLanguageName = (lang) => {
  const nativeName = languageNameMap.locale[lang]

  return (
    <span>{nativeName}</span>
  )
}

const getCellClass = (styleOption) => {
  switch (styleOption) {
    case 'Full width':
      return 'cell full-width'
    case '1/1':
      return 'cell'
    case '1/2':
      return 'cell large-6'
    case '1/3':
      return 'cell large-4'
    case '1/4':
      return 'cell large-3'
    case '2/3':
      return 'cell large-8'
    default:
      return 'cell'
  }
}

const getBackgroundClass = (section) => {
  let returnClass = ''
  const isVideo = getMediaType(section.backgroundImage.file.url) === 'mp4'

  switch (section.backgroundPosition.toLowerCase()) {
    case 'left':
      returnClass = 'left'
      break
    case 'center':
      returnClass = 'center'
      break
    case 'right':
      returnClass = 'right'
      break
    default:
      returnClass = 'center'
  }

  if (isVideo) {
    returnClass += ' section__bg--video'
  }

  return returnClass
}

const getSectionAlignment = (sectionAlignment) => {
  switch (sectionAlignment.toLowerCase()) {
    case 'left':
      return 'align-left'
    case 'center':
      return 'align-middle'
    case 'right':
      return 'align-right'
    default:
      return 'align-left'
  }
}

const mapAnimations = () => {
  const animatedEl = document.querySelectorAll('.js-gs-wrapper')

  gsap.registerPlugin(ScrollTrigger)

  if (animatedEl) {
    const animateFrom = (elem) => {
      gsap.fromTo(elem, {
        y: -100,
        autoAlpha: 0,
        opacity: 0,
      },
        {
          duration: 1,
          y: 0,
          opacity: 1,
          autoAlpha: 1,
          delay: .5,
          ease: "expo",
          overwrite: "auto"
        });
    }

    const hideAnimation = (elem) => {
      gsap.set(elem, { autoAlpha: 0 });
    }

    const showAnimation = (elem) => {
      gsap.set(elem, { autoAlpha: 1 });
    }

    gsap.utils.toArray('.js-gs-wrapper').forEach(wrapper => {
      hideAnimation(wrapper)

      ScrollTrigger.create({
        fastScrollEnd: true,
        markers: false,
        trigger: wrapper,
        start: 'top bottom',
        toggleActions: 'play reset play reset',
        onEnter: () => {
          const fadeEl = wrapper.querySelector('.js-gs-animation')

          showAnimation(wrapper)
          animateFrom(fadeEl)
        },
        onEnterBack: () => {
          showAnimation(wrapper)
        },
        onLeave: () => {
          hideAnimation(wrapper)
          ScrollTrigger.refresh()
        },
        onLeaveBack: () => {
          wrapper.style.opacity = 0
          ScrollTrigger.refresh()
        },
      })
    })
  }

}

const removeEmoji = (str) => {
  if (str !== null) return str.replace(new RegExp('[\u00FF-\uFFFF]+', 'g'), '')
}

const getBrowserLanguage = (langProps) => {
  let inLanguages = false
  let matchedLanguage = null

  if (typeof navigator === 'undefined') {
    return false
  }

  const browserLang = navigator && navigator.language && navigator.language.split("-")[0]

  langProps.lang.forEach(currentLang => {
    if (currentLang.node.code.toLowerCase() === browserLang.toLowerCase()) {
      inLanguages = true
      matchedLanguage = currentLang.node.code
    }
  })

  if (browserLang !== langProps.defaultLang && inLanguages) {
    return matchedLanguage
  }

  return false
}

/**
 * Checks if content should be displayed based on tags and current locale
 * @param {Array} contentTags - Array of Contentful Tag objects
 * @param {string} currentLocale - Current locale (e.g. 'en-US', 'de', 'en')
 * @returns {boolean} - true if content should be shown
 */
export const shouldShowContent = (contentTags, currentLocale) => {
  // Default behavior: If no tags present, always show content
  if (!contentTags || contentTags.length === 0) {
    return true;
  }

  const tagNames = contentTags.map(tag => tag.name);

  // Error case: Both tags set - hide content and warn
  if (tagNames.includes('disableEnUs') && tagNames.includes('enableOnlyEnUs')) {
    console.warn('Fragment has both tags set - will be hidden:', { tags: tagNames });
    return false;
  }

  // Apply tag logic
  if (tagNames.includes('disableEnUs') && currentLocale === 'en-US') {
    return false; // Hide in en-US
  }

  if (tagNames.includes('enableOnlyEnUs') && currentLocale !== 'en-US') {
    return false; // Hide in all other locales
  }

  return true; // Default: Show content
};

/**
 * Finds tags for a specific Contentful entry ID
 * @param {string} contentful_id - The Contentful entry ID
 * @param {Array} allContentfulEntryTags - Array of all entry tags from GraphQL
 * @returns {Array} - Array of tags for the entry
 */
export const findTagsForContentfulId = (contentful_id, allContentfulEntryTags) => {
  const entryWithTags = allContentfulEntryTags?.nodes?.find(
    entry => entry.contentful_id === contentful_id
  );
  return entryWithTags ? entryWithTags.tags : [];
};

/**
 * Recursively filters fragments based on tags and current locale
 * @param {Object} fragment - The fragment to filter
 * @param {string} currentLocale - Current locale (e.g. 'en-US', 'de', 'en')
 * @param {Array} allContentfulEntryTags - Array of all entry tags from GraphQL
 * @returns {Object|null} - Filtered fragment or null if should be hidden
 */
export const filterFragmentByTags = (fragment, currentLocale, allContentfulEntryTags) => {
  if (!fragment) return null;

  const fragmentTags = findTagsForContentfulId(fragment.contentful_id, allContentfulEntryTags);
  const shouldShow = shouldShowContent(fragmentTags, currentLocale);

  if (!shouldShow) {
    return null;
  }

  let filteredFragment = { ...fragment };

  if (fragment.fragments && Array.isArray(fragment.fragments)) {
    filteredFragment.fragments = fragment.fragments
      .map(subFragment => filterFragmentByTags(subFragment, currentLocale, allContentfulEntryTags))
      .filter(Boolean);
  }

  return filteredFragment;
};

export {
  isMobile,
  getBackgroundClass,
  getBrowserLanguage,
  getCellClass,
  getLanguageName,
  getMediaType,
  getSectionAlignment,
  mapAnimations,
  removeEmoji
}
